import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Play, Users, Settings, Trophy, QrCode, Copy, Eye, RotateCcw, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';
import GameSession from './GameSession';
import PlayerInterface from './PlayerInterface';

interface Question {
  id: number;
  question_text: string;
  answer_text: string;
  question_type: string;
  subject: string;
  difficulty: string;
  tags: string[];
}

interface GameSession {
  id: number;
  host_id: number;
  game_name: string;
  room_code: string;
  question_ids: string;
  current_question_index: number;
  game_status: string;
  question_time_limit: number;
  question_started_at: string;
  created_at: string;
}

const GameifyManager: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('create');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [gameSessions, setGameSessions] = useState<GameSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeGameId, setActiveGameId] = useState<number | null>(null);
  const [isPlayerView, setIsPlayerView] = useState(false);
  const [playerRoomCode, setPlayerRoomCode] = useState('');
  const [deletingGameId, setDeletingGameId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<GameSession | null>(null);

  // Game creation form
  const [gameName, setGameName] = useState('');
  const [timeLimit, setTimeLimit] = useState(30);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  useEffect(() => {
    fetchQuestions();
    fetchGameSessions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(16057, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: user ? [{ name: 'teacher_id', op: 'Equal', value: user.ID }] : []
      });

      if (error) throw error;

      const processedQuestions = data.List.map((q: any) => ({
        ...q,
        tags: JSON.parse(q.tags || '[]')
      }));

      setQuestions(processedQuestions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load questions from your pool',
        variant: 'destructive'
      });
    }
  };

  const fetchGameSessions = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(17213, {
        PageNo: 1,
        PageSize: 20,
        OrderByField: 'created_at',
        IsAsc: false,
        Filters: user ? [{ name: 'host_id', op: 'Equal', value: user.ID }] : []
      });

      if (error) throw error;
      setGameSessions(data.List);
    } catch (error) {
      console.error('Error fetching game sessions:', error);
    }
  };

  const generateRoomCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const createGame = async () => {
    if (!gameName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a game name',
        variant: 'destructive'
      });
      return;
    }

    if (selectedQuestions.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one question',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const roomCode = generateRoomCode();
      const { error } = await window.ezsite.apis.tableCreate(17213, {
        host_id: user?.ID,
        game_name: gameName,
        room_code: roomCode,
        question_ids: JSON.stringify(selectedQuestions),
        current_question_index: 0,
        game_status: 'waiting',
        question_time_limit: timeLimit,
        question_started_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Game "${gameName}" created with room code: ${roomCode}`
      });

      setGameName('');
      setSelectedQuestions([]);
      setActiveTab('sessions');
      fetchGameSessions();
    } catch (error) {
      console.error('Error creating game:', error);
      toast({
        title: 'Error',
        description: 'Failed to create game',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteGame = (game: GameSession) => {
    setGameToDelete(game);
    setDeleteDialogOpen(true);
  };

  const deleteGame = async () => {
    if (!gameToDelete) return;

    setDeletingGameId(gameToDelete.id);
    try {
      console.log('Starting deletion process for game:', gameToDelete.id);

      // First, delete all game responses for this session
      const responsesResult = await window.ezsite.apis.tablePage(17215, {
        PageNo: 1,
        PageSize: 1000,
        Filters: [{ name: 'game_session_id', op: 'Equal', value: gameToDelete.id }]
      });

      if (responsesResult.data && responsesResult.data.List.length > 0) {
        console.log(`Deleting ${responsesResult.data.List.length} game responses`);
        for (const response of responsesResult.data.List) {
          const { error: responseError } = await window.ezsite.apis.tableDelete(17215, { ID: response.ID });
          if (responseError) {
            console.error('Error deleting response:', responseError);
          }
        }
      }

      // Then, delete all participants for this session
      const participantsResult = await window.ezsite.apis.tablePage(17214, {
        PageNo: 1,
        PageSize: 1000,
        Filters: [{ name: 'game_session_id', op: 'Equal', value: gameToDelete.id }]
      });

      if (participantsResult.data && participantsResult.data.List.length > 0) {
        console.log(`Deleting ${participantsResult.data.List.length} participants`);
        for (const participant of participantsResult.data.List) {
          const { error: participantError } = await window.ezsite.apis.tableDelete(17214, { ID: participant.ID });
          if (participantError) {
            console.error('Error deleting participant:', participantError);
          }
        }
      }

      // Finally, delete the game session itself
      const { error: sessionError } = await window.ezsite.apis.tableDelete(17213, { ID: gameToDelete.id });
      if (sessionError) throw sessionError;

      toast({
        title: 'Success',
        description: `Game "${gameToDelete.game_name}" has been deleted successfully`
      });

      // Refresh the games list
      fetchGameSessions();
      setDeleteDialogOpen(false);
      setGameToDelete(null);
    } catch (error) {
      console.error('Error deleting game:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete game. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setDeletingGameId(null);
    }
  };

  const joinGameAsPlayer = () => {
    if (!playerRoomCode.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a room code',
        variant: 'destructive'
      });
      return;
    }
    setIsPlayerView(true);
  };

  const filteredQuestions = questions.filter((q) => {
    const matchesSubject = subjectFilter === 'all' || q.subject.toLowerCase().includes(subjectFilter.toLowerCase());
    const matchesDifficulty = difficultyFilter === 'all' || q.difficulty === difficultyFilter;
    return matchesSubject && matchesDifficulty;
  });

  const subjects = [...new Set(questions.map((q) => q.subject))];

  if (activeGameId) {
    return <GameSession gameId={activeGameId} onExit={() => setActiveGameId(null)} data-id="civ19kvoi" data-path="src/components/gameify/GameifyManager.tsx" />;
  }

  if (isPlayerView) {
    return <PlayerInterface roomCode={playerRoomCode} onExit={() => setIsPlayerView(false)} data-id="w91p6emic" data-path="src/components/gameify/GameifyManager.tsx" />;
  }

  return (
    <div className="space-y-6" data-id="czuy9bqrp" data-path="src/components/gameify/GameifyManager.tsx">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between" data-id="39ufle4ca" data-path="src/components/gameify/GameifyManager.tsx">

        <div data-id="xjl2agxg4" data-path="src/components/gameify/GameifyManager.tsx">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent" data-id="bgx13rtj9" data-path="src/components/gameify/GameifyManager.tsx">
            Game-ify
          </h1>
          <p className="text-muted-foreground mt-2" data-id="0xle1tnre" data-path="src/components/gameify/GameifyManager.tsx">
            Create engaging Kahoot-style quiz games for your students
          </p>
        </div>
        <div className="flex gap-2" data-id="t834du9wg" data-path="src/components/gameify/GameifyManager.tsx">
          <Dialog data-id="lyu89c9tj" data-path="src/components/gameify/GameifyManager.tsx">
            <DialogTrigger asChild data-id="astjkom5s" data-path="src/components/gameify/GameifyManager.tsx">
              <Button variant="outline" size="sm" data-id="fved5lab4" data-path="src/components/gameify/GameifyManager.tsx">
                <QrCode className="h-4 w-4 mr-2" data-id="5ertq4lkd" data-path="src/components/gameify/GameifyManager.tsx" />
                Join Game
              </Button>
            </DialogTrigger>
            <DialogContent data-id="r8c4wkc03" data-path="src/components/gameify/GameifyManager.tsx">
              <DialogHeader data-id="9t4wwzjp4" data-path="src/components/gameify/GameifyManager.tsx">
                <DialogTitle data-id="7lao828w0" data-path="src/components/gameify/GameifyManager.tsx">Join Game as Player</DialogTitle>
                <DialogDescription data-id="thd7k15oj" data-path="src/components/gameify/GameifyManager.tsx">
                  Enter the room code to join a game as a player
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4" data-id="h1bt1icti" data-path="src/components/gameify/GameifyManager.tsx">
                <div data-id="8o2do7fv0" data-path="src/components/gameify/GameifyManager.tsx">
                  <Label htmlFor="room-code" data-id="rlsnyl94y" data-path="src/components/gameify/GameifyManager.tsx">Room Code</Label>
                  <Input
                    id="room-code"
                    placeholder="Enter 6-digit room code"
                    value={playerRoomCode}
                    onChange={(e) => setPlayerRoomCode(e.target.value.toUpperCase())}
                    maxLength={6} data-id="9i5ao6qm5" data-path="src/components/gameify/GameifyManager.tsx" />

                </div>
                <Button onClick={joinGameAsPlayer} className="w-full" data-id="qcvt99x5f" data-path="src/components/gameify/GameifyManager.tsx">
                  Join Game
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-id="97bnumcyf" data-path="src/components/gameify/GameifyManager.tsx">
        <TabsList className="grid w-full grid-cols-3" data-id="8x0n710ys" data-path="src/components/gameify/GameifyManager.tsx">
          <TabsTrigger value="create" data-id="2semgab2s" data-path="src/components/gameify/GameifyManager.tsx">Create Game</TabsTrigger>
          <TabsTrigger value="sessions" data-id="ssltt8qxn" data-path="src/components/gameify/GameifyManager.tsx">My Games</TabsTrigger>
          <TabsTrigger value="results" data-id="23sy634lm" data-path="src/components/gameify/GameifyManager.tsx">Results</TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="space-y-6" data-id="75ytnzjdm" data-path="src/components/gameify/GameifyManager.tsx">
          <Card data-id="27a3iyv68" data-path="src/components/gameify/GameifyManager.tsx">
            <CardHeader data-id="aqcq8d2ok" data-path="src/components/gameify/GameifyManager.tsx">
              <CardTitle className="flex items-center gap-2" data-id="4kn3jema1" data-path="src/components/gameify/GameifyManager.tsx">
                <Play className="h-5 w-5" data-id="vuezuzytc" data-path="src/components/gameify/GameifyManager.tsx" />
                Create New Game
              </CardTitle>
              <CardDescription data-id="vmguyhq6n" data-path="src/components/gameify/GameifyManager.tsx">
                Set up a new quiz game using questions from your pool
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6" data-id="luzxaph39" data-path="src/components/gameify/GameifyManager.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="xu573rvgn" data-path="src/components/gameify/GameifyManager.tsx">
                <div data-id="9voexv09l" data-path="src/components/gameify/GameifyManager.tsx">
                  <Label htmlFor="game-name" data-id="ncvpr3iq0" data-path="src/components/gameify/GameifyManager.tsx">Game Name</Label>
                  <Input
                    id="game-name"
                    placeholder="Enter game name"
                    value={gameName}
                    onChange={(e) => setGameName(e.target.value)} data-id="io46j2p5n" data-path="src/components/gameify/GameifyManager.tsx" />

                </div>
                <div data-id="8x96t77m1" data-path="src/components/gameify/GameifyManager.tsx">
                  <Label htmlFor="time-limit" data-id="5ku63c1co" data-path="src/components/gameify/GameifyManager.tsx">Time Limit (seconds)</Label>
                  <Select value={timeLimit.toString()} onValueChange={(value) => setTimeLimit(parseInt(value))} data-id="ilfq99rf3" data-path="src/components/gameify/GameifyManager.tsx">
                    <SelectTrigger data-id="94z9nmyjg" data-path="src/components/gameify/GameifyManager.tsx">
                      <SelectValue data-id="uhdrp3c8v" data-path="src/components/gameify/GameifyManager.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="xfymvce8w" data-path="src/components/gameify/GameifyManager.tsx">
                      <SelectItem value="15" data-id="4x53yo23e" data-path="src/components/gameify/GameifyManager.tsx">15 seconds</SelectItem>
                      <SelectItem value="30" data-id="0v062slnb" data-path="src/components/gameify/GameifyManager.tsx">30 seconds</SelectItem>
                      <SelectItem value="60" data-id="42mhywql7" data-path="src/components/gameify/GameifyManager.tsx">60 seconds</SelectItem>
                      <SelectItem value="90" data-id="whigiatk0" data-path="src/components/gameify/GameifyManager.tsx">90 seconds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4" data-id="av6jqsrk6" data-path="src/components/gameify/GameifyManager.tsx">
                <h3 className="text-lg font-semibold" data-id="hu64fzwsm" data-path="src/components/gameify/GameifyManager.tsx">Select Questions</h3>
                
                <div className="flex gap-4" data-id="jha6tvmfg" data-path="src/components/gameify/GameifyManager.tsx">
                  <div className="flex-1" data-id="zbj58v55p" data-path="src/components/gameify/GameifyManager.tsx">
                    <Label htmlFor="subject-filter" data-id="abkhrzxeo" data-path="src/components/gameify/GameifyManager.tsx">Filter by Subject</Label>
                    <Select value={subjectFilter} onValueChange={setSubjectFilter} data-id="hdhkunqk5" data-path="src/components/gameify/GameifyManager.tsx">
                      <SelectTrigger data-id="vzkvuzzzn" data-path="src/components/gameify/GameifyManager.tsx">
                        <SelectValue placeholder="All subjects" data-id="tw4hgm5qv" data-path="src/components/gameify/GameifyManager.tsx" />
                      </SelectTrigger>
                      <SelectContent data-id="4pzc31eef" data-path="src/components/gameify/GameifyManager.tsx">
                        <SelectItem value="all" data-id="lb2qj7sia" data-path="src/components/gameify/GameifyManager.tsx">All subjects</SelectItem>
                        {subjects.map((subject) =>
                        <SelectItem key={subject} value={subject} data-id="ejwxs9c22" data-path="src/components/gameify/GameifyManager.tsx">{subject}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1" data-id="04fanlnyf" data-path="src/components/gameify/GameifyManager.tsx">
                    <Label htmlFor="difficulty-filter" data-id="juaraldor" data-path="src/components/gameify/GameifyManager.tsx">Filter by Difficulty</Label>
                    <Select value={difficultyFilter} onValueChange={setDifficultyFilter} data-id="eg43g5y8f" data-path="src/components/gameify/GameifyManager.tsx">
                      <SelectTrigger data-id="cujbn37yt" data-path="src/components/gameify/GameifyManager.tsx">
                        <SelectValue placeholder="All difficulties" data-id="u0lhx5xzx" data-path="src/components/gameify/GameifyManager.tsx" />
                      </SelectTrigger>
                      <SelectContent data-id="zez6k97ik" data-path="src/components/gameify/GameifyManager.tsx">
                        <SelectItem value="all" data-id="ilmp5gbm3" data-path="src/components/gameify/GameifyManager.tsx">All difficulties</SelectItem>
                        <SelectItem value="easy" data-id="ntx9tiw1d" data-path="src/components/gameify/GameifyManager.tsx">Easy</SelectItem>
                        <SelectItem value="medium" data-id="t7it7to56" data-path="src/components/gameify/GameifyManager.tsx">Medium</SelectItem>
                        <SelectItem value="hard" data-id="3iss8vaoq" data-path="src/components/gameify/GameifyManager.tsx">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto" data-id="hcztib9vy" data-path="src/components/gameify/GameifyManager.tsx">
                  {filteredQuestions.map((question) =>
                  <div
                    key={question.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedQuestions.includes(question.id) ?
                    'border-purple-500 bg-purple-50' :
                    'border-gray-200 hover:border-gray-300'}`
                    }
                    onClick={() => {
                      setSelectedQuestions((prev) =>
                      prev.includes(question.id) ?
                      prev.filter((id) => id !== question.id) :
                      [...prev, question.id]
                      );
                    }} data-id="0yr82zb4s" data-path="src/components/gameify/GameifyManager.tsx">

                      <div className="flex items-start justify-between" data-id="c2hk4ulc7" data-path="src/components/gameify/GameifyManager.tsx">
                        <div className="flex-1" data-id="6jj7l17ni" data-path="src/components/gameify/GameifyManager.tsx">
                          <div className="flex items-center gap-2 mb-2" data-id="uykhj0fz5" data-path="src/components/gameify/GameifyManager.tsx">
                            <Checkbox
                            checked={selectedQuestions.includes(question.id)}
                            onChange={() => {}} data-id="d2r51thel" data-path="src/components/gameify/GameifyManager.tsx" />

                            <Badge variant="outline" data-id="5wvjwfrdi" data-path="src/components/gameify/GameifyManager.tsx">{question.subject}</Badge>
                            <Badge variant="secondary" data-id="juaaw0dz6" data-path="src/components/gameify/GameifyManager.tsx">{question.difficulty}</Badge>
                          </div>
                          <p className="text-sm font-medium" data-id="bq5yvs1wo" data-path="src/components/gameify/GameifyManager.tsx">{question.question_text}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {selectedQuestions.length > 0 &&
                <div className="p-4 bg-blue-50 rounded-lg" data-id="nv0yyh9bk" data-path="src/components/gameify/GameifyManager.tsx">
                    <p className="text-sm text-blue-800" data-id="d4d601oei" data-path="src/components/gameify/GameifyManager.tsx">
                      Selected {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                }
              </div>

              <Button
                onClick={createGame}
                disabled={loading || selectedQuestions.length === 0}
                className="w-full" data-id="6kb1sc304" data-path="src/components/gameify/GameifyManager.tsx">

                {loading ? 'Creating Game...' : 'Create Game'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="space-y-6" data-id="rz1dcvg14" data-path="src/components/gameify/GameifyManager.tsx">
          <div className="grid gap-4" data-id="thq5eflfi" data-path="src/components/gameify/GameifyManager.tsx">
            {gameSessions.map((session) =>
            <motion.div
              key={session.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }} data-id="r0m3gr5cg" data-path="src/components/gameify/GameifyManager.tsx">

                <Card data-id="36h5c7au6" data-path="src/components/gameify/GameifyManager.tsx">
                  <CardContent className="p-6" data-id="hcd4l57iy" data-path="src/components/gameify/GameifyManager.tsx">
                    <div className="flex items-center justify-between" data-id="x45pimefb" data-path="src/components/gameify/GameifyManager.tsx">
                      <div data-id="gna966e3u" data-path="src/components/gameify/GameifyManager.tsx">
                        <h3 className="text-lg font-semibold" data-id="atthrt4vv" data-path="src/components/gameify/GameifyManager.tsx">{session.game_name}</h3>
                        <p className="text-sm text-muted-foreground" data-id="gic7tni5b" data-path="src/components/gameify/GameifyManager.tsx">
                          Room Code: <span className="font-mono font-bold" data-id="9xriuqcgf" data-path="src/components/gameify/GameifyManager.tsx">{session.room_code}</span>
                        </p>
                        <div className="flex items-center gap-4 mt-2" data-id="4btilnmzz" data-path="src/components/gameify/GameifyManager.tsx">
                          <Badge variant={session.game_status === 'active' ? 'default' : 'secondary'} data-id="0jph2tzxa" data-path="src/components/gameify/GameifyManager.tsx">
                            {session.game_status}
                          </Badge>
                          <span className="text-sm text-muted-foreground" data-id="hv93xrjzy" data-path="src/components/gameify/GameifyManager.tsx">
                            {JSON.parse(session.question_ids).length} questions
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2" data-id="bv4odo4zg" data-path="src/components/gameify/GameifyManager.tsx">
                        <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(session.room_code);
                          toast({
                            title: 'Copied!',
                            description: 'Room code copied to clipboard'
                          });
                        }} data-id="sclq0sq3b" data-path="src/components/gameify/GameifyManager.tsx">

                          <Copy className="h-4 w-4" data-id="jmlsoksyn" data-path="src/components/gameify/GameifyManager.tsx" />
                        </Button>
                        <Button
                        size="sm"
                        onClick={() => setActiveGameId(session.id)} data-id="uhrfy8x35" data-path="src/components/gameify/GameifyManager.tsx">

                          <Eye className="h-4 w-4 mr-2" data-id="ewjbpt8d0" data-path="src/components/gameify/GameifyManager.tsx" />
                          Open Game
                        </Button>
                        <Button
                        variant="outline"
                        size="sm"
                        onClick={() => confirmDeleteGame(session)}
                        disabled={deletingGameId === session.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="vyqd4erts" data-path="src/components/gameify/GameifyManager.tsx">
                          {deletingGameId === session.id ?
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" data-id="05t7lqgsc" data-path="src/components/gameify/GameifyManager.tsx" /> :

                        <Trash2 className="h-4 w-4" data-id="h08284olz" data-path="src/components/gameify/GameifyManager.tsx" />
                        }
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {gameSessions.length === 0 &&
          <Card data-id="qyfwvljss" data-path="src/components/gameify/GameifyManager.tsx">
              <CardContent className="p-8 text-center" data-id="etzfvjpj1" data-path="src/components/gameify/GameifyManager.tsx">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" data-id="vvnwtbeps" data-path="src/components/gameify/GameifyManager.tsx" />
                <h3 className="text-lg font-semibold mb-2" data-id="nfjpvfm9f" data-path="src/components/gameify/GameifyManager.tsx">No Games Yet</h3>
                <p className="text-muted-foreground" data-id="nv9kzzbvm" data-path="src/components/gameify/GameifyManager.tsx">
                  Create your first game to get started!
                </p>
              </CardContent>
            </Card>
          }
        </TabsContent>

        <TabsContent value="results" data-id="5funzfshy" data-path="src/components/gameify/GameifyManager.tsx">
          <Card data-id="ecy56bujz" data-path="src/components/gameify/GameifyManager.tsx">
            <CardContent className="p-8 text-center" data-id="p0g8sxob8" data-path="src/components/gameify/GameifyManager.tsx">
              <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" data-id="zyubjeg6i" data-path="src/components/gameify/GameifyManager.tsx" />
              <h3 className="text-lg font-semibold mb-2" data-id="16kha1tj7" data-path="src/components/gameify/GameifyManager.tsx">Game Results</h3>
              <p className="text-muted-foreground" data-id="326dmjbc0" data-path="src/components/gameify/GameifyManager.tsx">
                Results and analytics from your completed games will appear here.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} data-id="hczmn8mzt" data-path="src/components/gameify/GameifyManager.tsx">
        <DialogContent data-id="o9437gd3n" data-path="src/components/gameify/GameifyManager.tsx">
          <DialogHeader data-id="x842mzys4" data-path="src/components/gameify/GameifyManager.tsx">
            <DialogTitle data-id="u3oiyqrnq" data-path="src/components/gameify/GameifyManager.tsx">Delete Game</DialogTitle>
            <DialogDescription data-id="onfgaotk0" data-path="src/components/gameify/GameifyManager.tsx">
              Are you sure you want to delete "{gameToDelete?.game_name}"? This action cannot be undone and will permanently remove the game along with all participant data and responses.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4" data-id="yge914ps2" data-path="src/components/gameify/GameifyManager.tsx">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setGameToDelete(null);
              }}
              disabled={deletingGameId !== null} data-id="slwud5rgi" data-path="src/components/gameify/GameifyManager.tsx">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteGame}
              disabled={deletingGameId !== null} data-id="7gskwurbv" data-path="src/components/gameify/GameifyManager.tsx">
              {deletingGameId !== null ?
              <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" data-id="ci4vwmipa" data-path="src/components/gameify/GameifyManager.tsx" />
                  Deleting...
                </> :

              <>
                  <Trash2 className="h-4 w-4 mr-2" data-id="og5eov5cg" data-path="src/components/gameify/GameifyManager.tsx" />
                  Delete Game
                </>
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>);

};

export default GameifyManager;